import React, { useState, useEffect, useCallback, useRef } from 'react'
import iconTg1 from '../icons/tg-1.svg'
import iconX1 from '../icons/x-1.svg'
import {
  useAppKitAccount,
  useAppKitNetwork,
  useAppKit
} from '@reown/appkit/react'
import ChadHeaderLink from '../components/ChadHeaderLink'
import { Link } from 'react-router-dom'
import formatNumber, {
  imageUrl,
  apiUrl,
  coinNames,
  supportedChainIds
} from '../utils/constants.ts'
import { ingestHistory } from '../state/liveBuzzStore'
import buildBuyLink from '../utils/buildBuyLink.ts'

// simple in-memory cache so TopBar doesn't refetch on every page change
const HISTORY_TTL_MS = 45_000 // 45s reuse window
const historyCache = new Map() // chainId -> { ts: number, data: any[] }

// Persist a global epoch so the marquee animation can resume mid‑cycle across mounts
const CAROUSEL_EPOCH_MS = Date.now()

// --- Polling & item-key helpers for live update effects ---
const POLL_MS = 1500 // faster live-ish updates
const MAX_ITEMS = 50 // cap list for performance
const itemKey = it =>
  `${it.chainId}:${it.contract}:${it.buyer}:${it.type}:${it.amount}`

const SkeletonCarousel = () => {
  const placeholders = Array.from({ length: 10 })
  return (
    <div className="carousel-container z-[50] border border-[#2a2d36] bg-[#171a21]/80 overflow-hidden">
      <div
        className="carousel-track flex items-center gap-2 whitespace-nowrap"
        role="status"
        aria-live="polite"
        aria-label="loading recent trades"
      >
        {placeholders.map((_, i) => (
          <div
            key={`skeleton-${i}`}
            className="carousel-card inline-flex items-center gap-2 rounded-lg border border-[#2a2d36] bg-[#2a2d36]/40 px-3 h-9 text-[12px] leading-none flex-shrink-0 animate-pulse"
            style={{ margin: 0 }}
          >
            <span className="inline-flex items-center self-center w-10 h-3 rounded bg-white/10" />
            <span className="mx-1 inline-block h-1 w-1 rounded-full bg-white/10 self-center" />
            <span className="inline-flex items-center self-center w-3 h-3 rounded bg-white/10" />
            <span className="inline-flex items-center self-center w-14 h-3 rounded bg-white/10" />
            <span className="ml-1 inline-flex items-center self-center w-8 h-3 rounded bg-white/10" />
            <span className="mx-1 inline-block h-1 w-1 rounded-full bg-white/10 self-center" />
            <span className="inline-flex items-center self-center w-16 h-3 rounded bg-white/10" />
            <span className="inline-flex items-center self-center w-4 h-4 rounded-full bg-white/10 ring-1 ring-[#2a2d36]" />
          </div>
        ))}
      </div>
    </div>
  )
}

const TopBar = () => {
  const { chainId } = useAppKitNetwork()
  const { address, isConnected } = useAppKitAccount()
  const { open } = useAppKit()
  const [isExpanded, setIsExpanded] = useState(false)
  const buttonRef = useRef(null)
  const handleHamburgerClick = () => {
    setIsExpanded(prevExpanded => !prevExpanded)
  }
  const [history, setHistory] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const doubledHistory = React.useMemo(
    () => (history.length ? [...history, ...history] : []),
    [history]
  )
  const animDelay = React.useMemo(
    () => `-${Math.max(0, Date.now() - CAROUSEL_EPOCH_MS)}ms`,
    []
  )

  // --- Flashing/new-item effect state ---
  const flashMapRef = useRef(new Map()) // key -> expiry ms
  const prevKeysRef = useRef(new Set())
  const [flashVersion, setFlashVersion] = useState(0) // bump to re-render when flashes change

  // Helper to mark an item as new and schedule end of flash
  const markFlash = useCallback(k => {
    const expiry = Date.now() + 4000 // 4s glow
    flashMapRef.current.set(k, expiry)
    setFlashVersion(v => v + 1)
    setTimeout(() => {
      const cur = flashMapRef.current.get(k)
      if (cur && cur <= Date.now()) {
        flashMapRef.current.delete(k)
        setFlashVersion(v => v + 1)
      }
    }, 4200)
  }, [])
  const menuRef = useRef(null)

  useEffect(() => {
    // Add event listener for window resize
    const handleResize = () => {
      if (window.innerWidth >= 640) {
        setIsExpanded(false)
      }
    }

    window.addEventListener('resize', handleResize)
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller

    const fetchData = async () => {
      // Check warm cache first to avoid flashing + network on route changes
      const cached = historyCache.get(chainId)
      const now = Date.now()
      if (cached && now - cached.ts < HISTORY_TTL_MS) {
        setHistory(cached.data)
        setIsLoading(false)
        return
      }
      setIsLoading(true)
      try {
        const res = await fetch(apiUrl + `/api/getHistory/` + chainId, {
          method: 'GET',
          signal
        })
        if (!res.ok) {
          setHistory([])
          historyCache.set(chainId, { ts: Date.now(), data: [] })
          return
        }
        const data = await res.json()
        try {
          ingestHistory(chainId, data, 6000)
        } catch {}
        if (Array.isArray(data) && data.length > 0) {
          let historyData = data
            .filter(item => Number(item.chainId) === Number(chainId))
            .map(item => ({
              chainId: item.chainId,
              amount: item.amount,
              buyer: item.buyer,
              name: item.name,
              token: item.token,
              type: item.type,
              contract: item.contract,
              avatarUrl: imageUrl + 'profile-' + item.buyer + '.png'
            }))

          // Cap list size
          if (historyData.length > MAX_ITEMS)
            historyData = historyData.slice(0, MAX_ITEMS)

          // Diff keys to detect new items without recreating unchanged ones
          const nextKeys = new Set(historyData.map(itemKey))
          const prevKeys = prevKeysRef.current
          for (const k of nextKeys) if (!prevKeys.has(k)) markFlash(k)
          prevKeysRef.current = nextKeys

          setHistory(historyData)
          historyCache.set(chainId, { ts: Date.now(), data: historyData })
        } else {
          setHistory([])
          historyCache.set(chainId, { ts: Date.now(), data: [] })
        }
      } catch (e) {
        if (e.name === 'AbortError') return
        console.error(e)
        setIsLoading(false)
      } finally {
        // ensure loading flag resets even on non-OK responses
        setIsLoading(false)
      }
    }

    fetchData()
    return () => controller.abort()
  }, [chainId, markFlash])

  // Light refresh on window focus without flashing: revalidate if cache is cold, but don't show skeleton if data present
  useEffect(() => {
    const onFocus = () => {
      const cached = historyCache.get(chainId)
      const now = Date.now()
      if (!cached || now - cached.ts >= HISTORY_TTL_MS) {
        // silent revalidate: keep current UI, refresh cache in background
        ;(async () => {
          try {
            const res = await fetch(apiUrl + `/api/getHistory/` + chainId)
            if (!res.ok) return
            const data = await res.json()
            try {
              ingestHistory(chainId, data, 6000)
            } catch {}
            let historyData = Array.isArray(data)
              ? data
                  .filter(item => Number(item.chainId) === Number(chainId))
                  .map(item => ({
                    chainId: item.chainId,
                    amount: item.amount,
                    buyer: item.buyer,
                    name: item.name,
                    token: item.token,
                    type: item.type,
                    contract: item.contract,
                    avatarUrl: imageUrl + 'profile-' + item.buyer + '.png'
                  }))
              : []
            if (historyData.length > MAX_ITEMS)
              historyData = historyData.slice(0, MAX_ITEMS)
            const nextKeys = new Set(historyData.map(itemKey))
            const prevKeys = prevKeysRef.current
            for (const k of nextKeys) if (!prevKeys.has(k)) markFlash(k)
            prevKeysRef.current = nextKeys
            historyCache.set(chainId, { ts: Date.now(), data: historyData })
            setHistory(historyData)
          } catch (_) {}
        })()
      }
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [chainId, markFlash])

  // --- Live polling effect for new items, buzzing and flashing ---
  useEffect(() => {
    let cancelled = false
    const tick = async () => {
      if (document.hidden) return
      try {
        const res = await fetch(apiUrl + `/api/getHistory/` + chainId)
        if (!res.ok) return
        const data = await res.json()
        try {
          ingestHistory(chainId, data, 6000)
        } catch {}
        let historyData = Array.isArray(data)
          ? data
              .filter(item => Number(item.chainId) === Number(chainId))
              .map(item => ({
                chainId: item.chainId,
                amount: item.amount,
                buyer: item.buyer,
                name: item.name,
                token: item.token,
                type: item.type,
                contract: item.contract,
                avatarUrl: imageUrl + 'profile-' + item.buyer + '.png'
              }))
          : []
        if (historyData.length > MAX_ITEMS)
          historyData = historyData.slice(0, MAX_ITEMS)
        const nextKeys = new Set(historyData.map(itemKey))
        const prevKeys = prevKeysRef.current
        let changed = false
        if (nextKeys.size !== prevKeys.size) changed = true
        else {
          for (const k of nextKeys)
            if (!prevKeys.has(k)) {
              changed = true
              break
            }
        }
        if (changed && !cancelled) {
          for (const k of nextKeys) if (!prevKeys.has(k)) markFlash(k)
          prevKeysRef.current = nextKeys
          setHistory(historyData)
          historyCache.set(chainId, { ts: Date.now(), data: historyData })
        }
      } catch (_) {}
    }
    const id = setInterval(tick, POLL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [chainId, markFlash])

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsExpanded(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        setIsExpanded(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    if (!isExpanded && buttonRef.current) {
      buttonRef.current.focus()
    }
  }, [isExpanded])

  let currentPath = window.location.pathname

  return (
    <>
      {/* <LeftBar
        isExpanded={isExpanded}
        onHamburgerClick={handleHamburgerClick}
      /> */}
      <div className="top-bar">
        {/* {sortedList.map(({ name, address, trending }, i) => ( */}
        {isLoading ? (
          <SkeletonCarousel />
        ) : history.length > 0 ? (
          <div className="carousel-container z-[50] border border-[#2a2d36] bg-[#171a21]/80 overflow-hidden">
            <div
              className="carousel-track flex items-center gap-2 whitespace-nowrap"
              role="list"
              aria-label="recent trades"
              style={{ animationDelay: animDelay }}
            >
              {doubledHistory.map(
                (
                  {
                    chainId,
                    amount,
                    buyer,
                    name,
                    token,
                    contract,
                    type,
                    avatarUrl
                  },
                  i
                ) => (
                  <div
                    className={
                      `carousel-card inline-flex items-center gap-2 rounded-lg border border-[#2a2d36] bg-[#2a2d36]/40 px-3 h-9 text-[12px] leading-none text-[#f8ffe8]/90 tracking-tight hover:bg-[#2a2d36] hover:border-[#a135bb] active:scale-[0.99] transition-all duration-200 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#a135bb]/40 ` +
                      (flashMapRef.current.get(
                        itemKey({ chainId, contract, buyer, type, amount })
                      ) &&
                      flashMapRef.current.get(
                        itemKey({ chainId, contract, buyer, type, amount })
                      ) > Date.now()
                        ? ' hk-new'
                        : ' hk-buzz')
                    }
                    style={{ margin: 0 }}
                    key={`${itemKey({
                      chainId,
                      contract,
                      buyer,
                      type,
                      amount
                    })}-${i < history.length ? 'A' : 'B'}`}
                    role="listitem"
                  >
                    <Link
                      className="top-bar-address inline-flex items-center self-center leading-none text-[#f8ffe8]/70 hover:text-white"
                      to={'/profile/?address=' + buyer}
                      target={currentPath.includes('profile') ? '_blank' : ''}
                      rel="noreferrer"
                      title={buyer}
                    >
                      {buyer.slice(0, 2) + buyer.slice(-3)}
                    </Link>
                    <span className="mx-1 inline-block h-1 w-1 rounded-full bg-white/20 self-center"></span>
                    <span
                      className={
                        (type === 'bought'
                          ? 'text-green-500'
                          : 'text-red-500') +
                        ' drop-shadow-sm inline-flex items-center self-center leading-none'
                      }
                    >
                      {type === 'bought' ? '+' : '-'}
                    </span>
                    <span
                      className="font-mono tabular-nums text-[#f8ffe8] self-center leading-none"
                      title={String(formatNumber(Number(amount)))}
                    >
                      {formatNumber(Number(amount))}
                    </span>
                    <span className="ml-1 opacity-70 uppercase inline-flex items-center self-center leading-none">
                      {type === 'bought' ? coinNames[chainId] : 'Token'}
                    </span>
                    <span className="mx-1 inline-block h-1 w-1 rounded-full bg-white/20 self-center"></span>
                    <Link
                      className="top-bar-address inline-flex items-center text-[#f8ffe8]/80 hover:text-white"
                      to={buildBuyLink(
                        chainId,
                        contract,
                        Number(chainId) === 964 ? 'v2' : 'v4'
                      )}
                      rel="noreferrer"
                      title={name}
                    >
                      <span
                        className="top-bar-token mr-2 inline-flex items-center leading-none"
                        style={{ marginTop: 0, marginLeft: 0 }}
                      >
                        {name.length > 6 ? `${name.slice(0, 6)}` : name}
                      </span>
                      <img
                        src={
                          Number(chainId) === 964
                            ? imageUrl + contract + '-logo.png'
                            : imageUrl + contract + '-' + chainId + '-logo.png'
                        }
                        alt={`${name} logo`}
                        className="w-4 h-4 rounded-full ring-1 ring-[#2a2d36]"
                        aria-hidden="true"
                      />
                    </Link>
                  </div>
                )
              )}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
      <div className="absolute top-0 left-0 w-full z-50 px-2 bg-[#8c00f5]/0">
        <div
          className={`my-0 m-auto w-full items-center ${
            isExpanded ? 'pb-2 px-2' : 'px-2'
          }`}
        >
          <div className="flex justify-between">
            <div className="flex flex-row items-center">
              <ChadHeaderLink className="w-[220px] h-[70px]" />
              <div
                className="lg:flex hidden flex-row gap-4 ml-8"
                style={{ fontFamily: 'Bricolage Grotesque,  sans-serif' }}
              >
                <Link
                  to="/board"
                  className={`relative inline-flex items-center justify-center rounded-xl border px-3 py-1 text-[14px] transition-all duration-200 will-change-transform focus:outline-none focus:ring-2 focus:ring-[#a135bb]/30 hover:scale-[1.02] after:content-[''] after:absolute after:-bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-0.5 after:w-6 after:rounded-full after:transition-opacity after:duration-200 ${
                    currentPath === '/' || currentPath === '/board'
                      ? 'border-[#2a2d36] bg-[#2a2d36] text-white after:bg-[#a135bb] after:opacity-100'
                      : 'border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/90 hover:bg-[#2a2d36] hover:border-[#a135bb] after:opacity-0'
                  }`}
                  onClick={() => setIsExpanded(false)}
                >
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 9.75L12 3l9 6.75V21a.75.75 0 01-.75.75H3.75A.75.75 0 013 21V9.75z"
                      />
                    </svg>
                    home
                  </>
                </Link>
                <Link
                  to="/create"
                  className={`relative inline-flex items-center justify-center rounded-xl border px-3 py-1 text-[14px] transition-all duration-200 will-change-transform focus:outline-none focus:ring-2 focus:ring-[#a135bb]/30 hover:scale-[1.02] after:content-[''] after:absolute after:-bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-0.5 after:w-6 after:rounded-full after:transition-opacity after:duration-200 ${
                    currentPath === '/create'
                      ? 'border-[#2a2d36] bg-[#2a2d36] text-white after:bg-[#a135bb] after:opacity-100'
                      : 'border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/90 hover:bg-[#2a2d36] hover:border-[#a135bb] after:opacity-0'
                  }`}
                  onClick={() => setIsExpanded(false)}
                >
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    new
                  </>
                </Link>
                <Link
                  to={'/profile/?address=' + address}
                  className={`relative inline-flex items-center justify-center rounded-xl border px-3 py-1 text-[14px] transition-all duration-200 will-change-transform focus:outline-none focus:ring-2 focus:ring-[#a135bb]/30 hover:scale-[1.02] after:content-[''] after:absolute after:-bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-0.5 after:w-6 after:rounded-full after:transition-opacity after:duration-200 ${
                    currentPath.includes('/profile')
                      ? 'border-[#2a2d36] bg-[#2a2d36] text-white after:bg-[#a135bb] after:opacity-100'
                      : 'border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/90 hover:bg-[#2a2d36] hover:border-[#a135bb] after:opacity-0'
                  }`}
                  onClick={() => setIsExpanded(false)}
                >
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 12c2.485 0 4.5-2.015 4.5-4.5S14.485 3 12 3 7.5 5.015 7.5 7.5 9.515 12 12 12z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.5 21a8.988 8.988 0 0115 0"
                      />
                    </svg>
                    profile
                  </>
                </Link>
                <Link
                  to="/faq"
                  className={`relative inline-flex items-center justify-center rounded-xl border px-3 py-1 text-[14px] transition-all duration-200 will-change-transform focus:outline-none focus:ring-2 focus:ring-[#a135bb]/30 hover:scale-[1.02] after:content-[''] after:absolute after:-bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-0.5 after:w-6 after:rounded-full after:transition-opacity after:duration-200 ${
                    currentPath === '/faq'
                      ? 'border-[#2a2d36] bg-[#2a2d36] text-white after:bg-[#a135bb] after:opacity-100'
                      : 'border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/90 hover:bg-[#2a2d36] hover:border-[#a135bb] after:opacity-0'
                  }`}
                  onClick={() => setIsExpanded(false)}
                >
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.25 9.75h7.5m-7.5 3h4.5m-9 4.5V6.75A2.25 2.25 0 016.75 4.5h10.5A2.25 2.25 0 0119.5 6.75V18l-3-3H6.75A2.25 2.25 0 014.5 13.5z"
                      />
                    </svg>
                    faq
                  </>
                </Link>
              </div>
            </div>
            <div className="flex flex-row items-center gap-4">
              <div className="navConnectButtonBox">
                <button
                  type="button"
                  onClick={() =>
                    open({ view: isConnected ? 'Account' : 'Connect' })
                  }
                  className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl border border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8] hover:bg-[#2a2d36] hover:border-[#a135bb] text-[13px] sm:text-[14px] whitespace-nowrap shrink-0 focus:outline-none focus:ring-2 focus:ring-[#a135bb]/30"
                  aria-label="Connect Wallet"
                >
                  {isConnected ? (
                    <span>
                      {address
                        ? `${address.slice(0, 4)}…${address.slice(-3)}`
                        : 'Account'}
                    </span>
                  ) : (
                    <>
                      <span className="sm:hidden">connect</span>
                      <span className="hidden sm:inline">connect wallet</span>
                    </>
                  )}
                </button>
              </div>
              <button
                ref={buttonRef}
                className="relative w-[32px] h-[32px] flex lg:hidden items-center justify-center group"
                onClick={handleHamburgerClick}
                aria-label="Toggle menu"
                aria-expanded={isExpanded}
                aria-controls="topbar-mobile-menu"
              >
                <div
                  className="absolute w-6 h-[2px] bg-white transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
                  style={{
                    transformOrigin: 'center',
                    transform: isExpanded
                      ? 'translateY(0) rotate(45deg)'
                      : 'translateY(-6px) rotate(0deg)',
                    opacity: 1
                  }}
                />
                <div
                  className="absolute w-6 h-[2px] bg-white transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
                  style={{
                    transformOrigin: 'center',
                    transform: isExpanded ? 'scaleX(0.4)' : 'scaleX(1)',
                    opacity: isExpanded ? 0 : 1
                  }}
                />
                <div
                  className="absolute w-6 h-[2px] bg-white transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
                  style={{
                    transformOrigin: 'center',
                    transform: isExpanded
                      ? 'translateY(0) rotate(-45deg)'
                      : 'translateY(6px) rotate(0deg)',
                    opacity: 1
                  }}
                />
              </button>
            </div>
          </div>
          {isExpanded && (
            <div
              ref={menuRef}
              id="topbar-mobile-menu"
              role="menu"
              aria-label="Main menu"
              className="absolute left-0 top-[72px] w-full z-50 bg-[#171a21] shadow-xl border-t border-[#333] animate-fade-in"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex flex-col gap-4 py-5 px-4 sm:px-6 font-medium text-[#f8ffe8]">
                <Link
                  to="/board"
                  role="menuitem"
                  className="mobile-menu-link inline-flex items-center justify-center rounded-xl border border-[#2a2d36] bg-[#2a2d36]/40 px-4 py-2 text-[#f8ffe8] hover:bg-[#2a2d36] hover:border-[#a135bb] transition-all duration-200 will-change-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#a135bb]/30"
                  onClick={() => setIsExpanded(false)}
                >
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 9.75L12 3l9 6.75V21a.75.75 0 01-.75.75H3.75A.75.75 0 013 21V9.75z"
                      />
                    </svg>
                    home
                  </>
                </Link>
                <Link
                  to="/create"
                  role="menuitem"
                  className="mobile-menu-link inline-flex items-center justify-center rounded-xl border border-[#2a2d36] bg-[#2a2d36]/40 px-4 py-2 text-[#f8ffe8] hover:bg-[#2a2d36] hover:border-[#a135bb] transition-all duration-200 will-change-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#a135bb]/30"
                  onClick={() => setIsExpanded(false)}
                >
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    new
                  </>
                </Link>
                <Link
                  to={'/profile/?address=' + address}
                  role="menuitem"
                  className="mobile-menu-link inline-flex items-center justify-center rounded-xl border border-[#2a2d36] bg-[#2a2d36]/40 px-4 py-2 text-[#f8ffe8] hover:bg-[#2a2d36] hover:border-[#a135bb] transition-all duration-200 will-change-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#a135bb]/30"
                  onClick={() => setIsExpanded(false)}
                >
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 12c2.485 0 4.5-2.015 4.5-4.5S14.485 3 12 3 7.5 5.015 7.5 7.5 9.515 12 12 12z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.5 21a8.988 8.988 0 0115 0"
                      />
                    </svg>
                    profile
                  </>
                </Link>
                <Link
                  to="/faq"
                  role="menuitem"
                  className="mobile-menu-link inline-flex items-center justify-center rounded-xl border border-[#2a2d36] bg-[#2a2d36]/40 px-4 py-2 text-[#f8ffe8] hover:bg-[#2a2d36] hover:border-[#a135bb] transition-all duration-200 will-change-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#a135bb]/30"
                  onClick={() => setIsExpanded(false)}
                >
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.25 9.75h7.5m-7.5 3h4.5m-9 4.5V6.75A2.25 2.25 0 016.75 4.5h10.5A2.25 2.25 0 0119.5 6.75V18l-3-3H6.75A2.25 2.25 0 014.5 13.5z"
                      />
                    </svg>
                    faq
                  </>
                </Link>
                <div className="flex gap-4 justify-center mt-4 rounded-xl border border-[#2a2d36] bg-[#2a2d36]/40 p-3">
                  <a
                    href="https://x.com/hotkeyswap"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={iconX1} alt="x" className="w-[24px] h-[24px]" />
                  </a>
                  <a
                    href="https://t.me/hotkeyswap"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      src={iconTg1}
                      alt="telegram"
                      className="w-[24px] h-[24px]"
                    />
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default TopBar
